(ns dre.widgets.cooccurrence-matrix.events
  (:require
   [applied-science.js-interop :as j]
   [dre.fx :as-alias dre.fx]
   [clojure.string :as str]
   [dre.coll :refer [distinct-by]]
   [dre.components.searchbar.util :as searchbar-util]
   [dre.components.searchbar.suggestion-util :refer [new-suggestions]]
   [dre.i18n.views :refer [tr]]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.components.widget.events :as wevents]
   [dre.page-util :refer [strip-zerowidth-utf8]]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(def db-path [:widgets :cooccurrences-matrix])

(def id :cooccurrences-matrix)

(def filter-key id)
(def header-row-filter-key (keyword (str id "row")))
(def header-col-filter-key (keyword (str id "col")))

(defn ->pills
  [p1 p2]
  {:pill/pills (concat p1 p2)
   :pill/type :and})

(defn cells-equal?
  [old new]
  (= [(:cols new) (:rows new)]
     [(:cols old) (:rows old)]))

(defn count-matrix
  [data]
  (let [json-vector-to-edn-map
        (map (fn [[k v]]
               [(mapv
                 (fn [subject]
                      ;; results only indexed in view by uris
                   (mapv :pill/query subject))
                 k)
                v]))]
    (into
     {}
     json-vector-to-edn-map
     (:counts data))))

(defn relative-matrix [cm]
  (let [max-count (apply max (vals cm))]
    (zipmap (keys cm)
            (if (zero? max-count)
              (repeat 0)
              (map (fn [v]
                     (-> (/ v max-count)
                         ;; limit after decimal points
                         (.toFixed 5)
                         ;; do not introduce a decimal point for integers
                         (js/parseFloat)))
                   (vals cm))))))


(defn to-sankey [matrix-pills result-matrix]
  (let [collect (fn [_node nodes-arr links-arr]
                  (let [_ (doseq [row (:rows matrix-pills)]
                            (when (not (some (fn [node]
                                               (= (.-name node) (str "r" (:pill/query (first row)))))
                                             nodes-arr))
                              (.push nodes-arr #js {:pos :row :pill-q row :label (:pill/label (first row)) :name (str "r" (:pill/query (first row))) :type (:pill/query (first row))}))
                            (doseq [col (:cols matrix-pills)]
                              (when (not (some (fn [node]
                                                 (= (.-name node) (str "c" (:pill/query (first col)))))
                                               nodes-arr))
                                (.push nodes-arr #js {:pos :col :pill-q col :label (:pill/label (first col)) :name (str "c" (:pill/query (first col))) :type (:pill/query (first col))}))
                              (let [result (get result-matrix
                                                ;; results indexed only by uris
                                                [(mapv :pill/query row)
                                                 (mapv :pill/query col)])
                                    val (+ 0.00001 (:val (get result :counts)))]
                                (if-let [existing-link (some
                                                        (fn [link]
                                                          (when (and (= (.-source link) (str "r" (:pill/query (first row))))
                                                                     (= (.-target link) (str "c" (:pill/query (first col)))))
                                                            link))
                                                        links-arr)]
                                  ;; update link
                                  (j/assoc! existing-link :value (+ (j/get existing-link :value)
                                                                    val))
                                  ;; create link
                                  (.push links-arr #js {:source (str "r" (:pill/query (first row)))
                                                        :target (str "c" (:pill/query (first col)))
                                                        :names #js [(str "r" (:pill/query (first row))) (str "c" (:pill/query (first col)))]
                                                        :value val})))))]
                    [nodes-arr links-arr]))
        [nodes-in links-in] (collect matrix-pills #js [] #js [])]
    (if (zero? (count links-in))
      nil ;; no results
      #js {:nodes nodes-in
           :links links-in})))

(defn prepare-data
  [data f filter-key]
  (let [cm (count-matrix data)
        rm (relative-matrix cm)
        v-fn
        (fn [_k v]
          {:val v
           :active-fn
           (fn [p1 p2]
             (let [f? (contains? f (->pills p1 p2))]
               (if (seq f) (if f? :active :inactive) :none)))
           :click-fn
           (fn [p1 p2]
             (fn [_e]
               (rf/dispatch [::search-events/widget-filter-select
                             filter-key (->pills p1 p2)])))})
        under-key
        (fn [k m]
          (zipmap (keys m)
                  (map (fn [v]
                         {k (v-fn k v)})
                       (vals m))))]
    (merge-with merge
                (under-key :counts cm)
                (under-key :relative rm))))

(defn data-filters
  [db key]
  (->> db
       search-events/db-path
       :widget-filters
       key))

(defn selected-data?
  [db]
  (let [x (concat (data-filters db filter-key) (data-filters db header-row-filter-key) (data-filters db header-col-filter-key))]
    (not-empty x)))

;; -- Events

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :state :loading)}))

(rf/reg-event-fx
 ::init
 [rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params-fn]}]]
  (if (or (= last-selected-filter-key filter-key)
          (= last-selected-filter-key header-col-filter-key)
          (= last-selected-filter-key header-row-filter-key))
    {:db (assoc-in db (concat db-path [:state]) nil)
     :dispatch after-fx}
    (let [coocc-db (get db db-path)
          widget-db (get db wevents/db-path)
          selection (get-in widget-db [id :type])
          default-option (or selection [:top-o :top-i])
          params
          (-> (pills-api-params-fn [filter-key header-col-filter-key header-row-filter-key])
              (assoc :algo "mutual-information"
                     :k 5))]
      {:db (assoc-in db (concat db-path [:state]) :loading)
       ::network/request-with-abort [{:endpoint :widgets/cooccurrences-matrix-significant-forest}
                                     {:body (stringify-keys-with-namespaces params)
                                      :on-success [::success-settings {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                                                                       :pills-api-params-fn pills-api-params-fn :default-option default-option}]
                                      :on-failure [::failure {:after-fx after-fx}]}]}))))


(rf/reg-event-fx
 ::cooccurrences-matrix
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [after-fx
             pills-api-params-fn
             result-range
             cols
             rows]]
  (let [params (merge
                (pills-api-params-fn [filter-key header-col-filter-key header-row-filter-key])
                {:result-range result-range
                 :cols cols
                 :rows rows
                 :response/filter {:cooccurrences-matrix/cooccurrences-matrix true}})]
    (if (or (not (seq cols))
            (not (seq rows)))
      nil
      {:db (assoc db :state :loading)
       ::network/request-with-abort [{:endpoint :widgets/cooccurrences-matrix}
                                     {:body (stringify-keys-with-namespaces params)
                                      :on-success [::success-matrix {:after-fx after-fx}]
                                      :on-failure [::failure {:after-fx after-fx}]}]}))))

(rf/reg-event-fx
 ::matrix-pills-swap!
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx swap-fn pills-api-params-fn]}]]
  (let [path [:data :cols-and-rows]
        old (get-in db path) 
        new (update-in db path swap-fn)
        new* (get-in new path)] 
   {:db new
    :dispatch-n
    [[::cooccurrences-matrix after-fx pills-api-params-fn :all (:cols new*) (:rows new*)]]})))

(rf/reg-event-fx
 ::success-settings
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params-fn default-option ::fetch-fx/response]}]]
  (let [path [:data :cols-and-rows]
        matrix-pills (get-in db path)
        data (:body response)
        default-cell-count 4
        select-subjects
        (fn [all filter-key]
          (->> all
               filter-key ;; :top-o, :top-i, :top-c, :top-m
               :sense/meta
               (distinct-by :label)
               (take default-cell-count)
               (mapv (fn [p]
                       [{:pill/type :term
                         :pill/label (:label p)
                         :pill/query (:sense p)}]))))
        top-o (select-subjects data :top-o)
        top-i (select-subjects data :top-i)
        top-c (select-subjects data :top-c)
        cols (select-subjects data (first default-option))
        rows (select-subjects data (second default-option))
        update? (not (and (seq (:cols matrix-pills))
                          (seq (:rows matrix-pills))
                          last-selected-filter-key))]
    {:db (if update?
          (-> db
             (update-in (concat path [:by-category])
                        merge
                        {:top-o top-o
                         :top-i top-i
                         :top-c top-c}))
           db)
     :dispatch [::matrix-pills-swap!
                {:after-fx after-fx :last-selected-filter-key last-selected-filter-key
                 :pills-api-params-fn pills-api-params-fn :swap-fn (if update?
                                                                     (fn [matrix-pills]
                                                                       (assoc matrix-pills :cols cols :rows rows))
                                                                     (fn [matrix-pills]
                                                                       matrix-pills))}]})))

(rf/reg-event-fx
 ::success-matrix
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx  ::fetch-fx/response]}]]
  (let [res {:db {:state :success
                  :data (assoc (:data db) :matrix (:cooccurrences-matrix/cooccurrences-matrix (:body response)))}}
        res (if after-fx
              (assoc res :dispatch after-fx)
              res)] 
    res)))

(rf/reg-event-fx
 ::failure
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [after-fx ::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db {:state :failure
          :data nil}
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :cooc-matrix} opts)]]})))

(rf/reg-event-fx
 ::export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  (let [matrix-pills (get-in db [:data :cols-and-rows])
        data (get-in db [:data :matrix])
        rows (:rows matrix-pills)
        cols (:cols matrix-pills)
        cm (count-matrix data)
        col-labels (map (comp pr-str strip-zerowidth-utf8 :pill/label first) rows)
        row-uris (map #(mapv :pill/query %) rows)
        row-labels (map (comp pr-str strip-zerowidth-utf8 :pill/label first) cols)
        col-uris (map #(mapv :pill/query %) cols)
        cells (vec (for [row-uri row-uris]
                     (vec (for [col-uri col-uris]
                            (get cm [row-uri col-uri])))))
        csv-header (str/join "," (cons nil row-labels))
        csv-rows (map (fn [label cell-row]
                        (str/join "," (cons label cell-row)))
                      col-labels cells)
        csv (str/join "\n" (cons csv-header csv-rows))]
    (rf/dispatch [::dre.fx/download {:content csv
                                     :filename "co-occurrences-matrix.csv"
                                     :mimetype "text/csv;charset=UTF-8"}])
    nil)))

(rf/reg-event-fx
 ::set-add-new-sense?
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [row?]]
  (let [old-res (:add-new-sense? db)
        new-res (if row? :rows :cols)
        new-res (if (= old-res new-res) :none new-res)]
    {:db (assoc db :add-new-sense? new-res)})))

(rf/reg-event-fx
 ::on-searchbar-change
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [pills-api-params-fn search-query]]
  (let [add-new-sense? (:add-new-sense? db)
        new-pill (first (get-in search-query [:query/pills]))]
    (when new-pill
      {:db db
       :dispatch-n [[::set-add-new-sense? false]
                    [::matrix-pills-swap!
                     {:after-fx nil
                      :swap-fn (fn [matrix-pills]
                                 (update matrix-pills add-new-sense?
                                         (fn [cells]
                                           (concat (or cells []) [[new-pill]]))))
                      :pills-api-params-fn pills-api-params-fn}]]}))))

(rf/reg-event-fx
 ::fetch-suggestions
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} [db-root-path {:keys [input limit] :as opts}]]
  {:pre [(vector? db-root-path)
         (searchbar-util/has-keys? opts #{:input :caret-position :search-query})]}
  (if-let [suggestions (get-in db (conj db-root-path :suggestions input))]
    {:db (update-in db db-root-path
                    #(assoc-in % [:suggestions :current] suggestions))}
    {::network/request
     [{:endpoint :dictionary/suggestions}
      {:params {:query input
                :limit (or limit 10)}
       :envelope? false
       :on-success [::fetch-suggestions-on-success db-root-path opts]}]})))

(rf/reg-event-db
 ::fetch-suggestions-on-success
 [rf/trim-v]
 (fn-traced
  [db [db-root-path {:keys [input] :as opts} {:keys [body]}]]
  {:pre [(vector? db-root-path)
         (searchbar-util/has-keys? opts #{:input :caret-position :search-query})]}
  (let [suggestions (assoc opts :data body)
        {:keys [input caret-position search-query]} suggestions
        suggestions (new-suggestions #_input input
                                     #_idx caret-position
                                     #_mode "subjects-w-umbrella"
                                     #_pills (-> search-query :query/pills (or []))
                                     #_dict-pills (searchbar-util/->suggestion-pills suggestions)
                                     #_signals nil
                                     #_tags nil)]
    (update-in db db-root-path
               #(-> %
                    (assoc-in [:suggestions input] suggestions)
                    (assoc-in [:suggestions :current] suggestions))))))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))

(rf/reg-sub
 ::data
 :<- [::state]
 :<- [::search-events/widget-filters]
 (fn [[state widget-filters] _]
   (prepare-data (get-in state [:data :matrix]) (get widget-filters filter-key) filter-key)))

(rf/reg-sub
 ::data-sankey
 :<- [::state]
 (fn [state _]
   (to-sankey (-> (get-in state [:data :cols-and-rows])
                  (select-keys [:cols :rows]))
             (prepare-data (get-in state [:data :matrix]) [] filter-key))))

(rf/reg-sub
 ::status
 :<- [::state]
 (fn [db _]
   (:state db)))

(rf/reg-sub
 ::add-new-sense?
 :<- [::state]
 (fn [db _]
   (get db :add-new-sense? :none)))

(rf/reg-sub
 ::pre-fill-categories
 :<- [::state]
 (fn [state [_]]
   (get-in state [:data :cols-and-rows :by-category])))

(rf/reg-sub
 ::matrix-pills
 :<- [::state]
 (fn [state [_]]
   (-> (get-in state [:data :cols-and-rows])
       (select-keys [:cols :rows]))))

(rf/reg-sub
 ::used-in-search?
 selected-data?)